/**
 * Image Optimization Utility for Photo Report Generator
 * Reduces file size while maintaining quality and dimensions
 */

class ImageOptimizer {
  constructor() {
    this.defaultQuality = 0.8; // 80% quality - good balance between size and quality
    this.maxWidth = 1920; // Maximum width to prevent extremely large images
    this.maxHeight = 1080; // Maximum height to prevent extremely large images
  }

  /**
   * Optimize an image file by reducing its size while maintaining quality
   * @param {File} file - The image file to optimize
   * @param {Object} options - Optimization options
   * @returns {Promise<File>} - Optimized image file
   */
  async optimizeImage(file, options = {}) {
    const {
      quality = this.defaultQuality,
      maxWidth = this.maxWidth,
      maxHeight = this.maxHeight,
      maintainAspectRatio = true,
      outputFormat = 'image/jpeg' // JPEG typically provides better compression
    } = options;

    return new Promise((resolve, reject) => {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Create an image element
      const img = new Image();
      
      img.onload = () => {
        try {
          // Calculate new dimensions
          let { width, height } = this.calculateDimensions(
            img.width, 
            img.height, 
            maxWidth, 
            maxHeight, 
            maintainAspectRatio
          );

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw the image on canvas with new dimensions
          ctx.drawImage(img, 0, 0, width, height);

          // Convert canvas to blob with specified quality
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create a new File object with the optimized blob
                const optimizedFile = new File(
                  [blob], 
                  this.generateOptimizedFileName(file.name, outputFormat),
                  { 
                    type: outputFormat,
                    lastModified: Date.now()
                  }
                );
                
                console.log(`Image optimized: ${file.name}`);
                console.log(`Original size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
                console.log(`Optimized size: ${(optimizedFile.size / 1024 / 1024).toFixed(2)} MB`);
                console.log(`Size reduction: ${(((file.size - optimizedFile.size) / file.size) * 100).toFixed(1)}%`);
                console.log(`Dimensions: ${width}x${height}`);
                
                resolve(optimizedFile);
              } else {
                reject(new Error('Failed to optimize image'));
              }
            },
            outputFormat,
            quality
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for optimization'));
      };

      // Load the image
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Calculate optimal dimensions while maintaining aspect ratio
   */
  calculateDimensions(originalWidth, originalHeight, maxWidth, maxHeight, maintainAspectRatio) {
    if (!maintainAspectRatio) {
      return {
        width: Math.min(originalWidth, maxWidth),
        height: Math.min(originalHeight, maxHeight)
      };
    }

    // If image is already within limits, keep original dimensions
    if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
      return {
        width: originalWidth,
        height: originalHeight
      };
    }

    // Calculate scaling factor to fit within max dimensions
    const widthRatio = maxWidth / originalWidth;
    const heightRatio = maxHeight / originalHeight;
    const scalingFactor = Math.min(widthRatio, heightRatio);

    return {
      width: Math.round(originalWidth * scalingFactor),
      height: Math.round(originalHeight * scalingFactor)
    };
  }

  /**
   * Generate optimized file name
   */
  generateOptimizedFileName(originalName, outputFormat) {
    const nameWithoutExt = originalName.replace(/\.[^/.]+$/, "");
    const extension = outputFormat === 'image/jpeg' ? '.jpg' : 
                     outputFormat === 'image/png' ? '.png' : 
                     outputFormat === 'image/webp' ? '.webp' : '.jpg';
    
    return `${nameWithoutExt}_optimized${extension}`;
  }

  /**
   * Batch optimize multiple images
   * @param {FileList|Array} files - Array of image files
   * @param {Object} options - Optimization options
   * @returns {Promise<Array>} - Array of optimized files
   */
  async optimizeMultipleImages(files, options = {}) {
    const optimizedFiles = [];
    
    for (let i = 0; i < files.length; i++) {
      try {
        const optimizedFile = await this.optimizeImage(files[i], options);
        optimizedFiles.push(optimizedFile);
      } catch (error) {
        console.error(`Failed to optimize ${files[i].name}:`, error);
        // Keep original file if optimization fails
        optimizedFiles.push(files[i]);
      }
    }
    
    return optimizedFiles;
  }

  /**
   * Check if file is an image
   */
  isImageFile(file) {
    return file && file.type && file.type.startsWith('image/');
  }

  /**
   * Get optimization settings based on image type and size
   */
  getOptimalSettings(file) {
    const fileSizeMB = file.size / 1024 / 1024;
    
    // Adjust quality based on file size
    let quality = this.defaultQuality;
    if (fileSizeMB > 5) {
      quality = 0.7; // Lower quality for very large files
    } else if (fileSizeMB > 2) {
      quality = 0.75; // Slightly lower quality for large files
    }

    // Adjust max dimensions based on file size
    let maxWidth = this.maxWidth;
    let maxHeight = this.maxHeight;
    if (fileSizeMB > 10) {
      maxWidth = 1600;
      maxHeight = 900;
    }

    return {
      quality,
      maxWidth,
      maxHeight,
      outputFormat: file.type === 'image/png' ? 'image/png' : 'image/jpeg'
    };
  }
}

// Export for use in React components
export default ImageOptimizer;

